import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductItem, UpsellItem, BundleItem, FakeProduct } from '@lib/productsState/productsTypes';
import { WarrantyProduct } from './types';

export type CartItem = ProductItem | UpsellItem;

export type ProductCartItem = ProductItem & {
  type?: string;
  orderSummaryName?: string;
  status?: string;
  bundle_products: BundleItem[];
  generic_name?: string;
  fake_products?: FakeProduct[] | null;
};

const recalcProductsQuantity = (cartProducts: CartItem[]) => {
  return cartProducts.reduce((total, item) => total + Number(item.amount), 0);
};

type CartState = {
  loader: boolean;
  isCartOpen: boolean;
  isHeroVisible: boolean;
  finalPrice: number;
  country: string;
  cartProducts: ProductCartItem[];
  warrantyProduct: WarrantyProduct;
  productsQuantity: number;
  withShippingProtection: boolean;
  totalSavings: number;
  subTotal: number;
  hasFreeShipping: boolean;
  freeShippingPrice: number;
  loadLazyComponents: boolean;
};

const initialState: CartState = {
  loader: false,
  isCartOpen: false,
  isHeroVisible: false,
  finalPrice: 0,
  country: window.country,
  cartProducts: [],
  warrantyProduct: {
    warrantyState: false,
    preselect_warranty: false,
    price: 0,
    konnektive_id: '',
    sku: '',
    display_name: '',
  },
  productsQuantity: 0,
  withShippingProtection: true,
  totalSavings: 0,
  subTotal: 0,
  hasFreeShipping: false,
  freeShippingPrice: Number(window.productData?.shopping_cart?.tr_shopping_cart_amount_of_free_shipping_unlock) || 0,
  loadLazyComponents: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
    },
    loadLazy: (state) => {
      state.loadLazyComponents = true;
    },
    isHeroVisible: (state) => {
      state.isHeroVisible = !state.isHeroVisible;
    },
    setLoader: (state) => {
      state.loader = !state.loader;
    },

    toggleWarranty: (state, action: PayloadAction<boolean>) => {
      state.warrantyProduct.warrantyState = action.payload;
    },
    setWarrantyData: (state, action: PayloadAction<WarrantyProduct>) => {
      state.warrantyProduct = action.payload;
    },

    setTotalSavings: (state) => {
      const total = 0;
      state.totalSavings = total;
    },
    setFinalPrice: (state, action: PayloadAction<{ total: number; savings: number }>) => {
      state.finalPrice = action.payload.total;
      state.totalSavings = action.payload.savings;
    },
    setBundleUpsell: (state, action: PayloadAction<{ total: number; savings: number }>) => {
      state.finalPrice = action.payload.total;
      state.totalSavings = action.payload.savings;
    },
    setProductsQuantity: (state) => {
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    setSavingsQuantityPrice: (state) => {
      cartSlice.caseReducers.setTotalSavings(state);
      cartSlice.caseReducers.setProductsQuantity(state);
    },
    setCartProducts: (state, action: PayloadAction<ProductCartItem[]>) => {
      state.cartProducts = action.payload;
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    addItem: (state, action: PayloadAction<ProductCartItem>) => {
      const productId = action.payload.id;
      const indexOfProduct = state.cartProducts.findIndex((item) => item.id === productId);
      if (indexOfProduct !== -1) {
        const prevAmount = Number(state.cartProducts[indexOfProduct].amount);
        state.cartProducts[indexOfProduct].amount = prevAmount + Number(action.payload.amount);
      } else {
        state.cartProducts.push(action.payload);
      }
    },
    addMainProduct: (state, action: PayloadAction<{ item: ProductCartItem; isEmptyCart: boolean }>) => {
      const productId = action.payload.item.id;
      const indexOfProduct = state.cartProducts.findIndex((item) => item.id === productId);
      if (indexOfProduct == -1 && action.payload.isEmptyCart) {
        state.cartProducts.push(action.payload.item);
        state.warrantyProduct.price = action.payload.item.warranty_price || 0;
      } else {
        if (action.payload.isEmptyCart) {
          if (indexOfProduct == -1) {
            state.cartProducts.push(action.payload.item);
            state.warrantyProduct.price = action.payload.item.warranty_price || 0;
          } else {
            state.cartProducts[indexOfProduct].amount =
              Number(state.cartProducts[indexOfProduct].amount) + Number(action.payload.item.amount);
          }
        } else {
          const filteredProducts = state.cartProducts.filter((item) => item.type !== 'offer');
          state.cartProducts = [...filteredProducts, action.payload.item];
          state.warrantyProduct.price = action.payload.item.warranty_price || 0;
        }
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.cartProducts = state.cartProducts.filter((item) => {
        if ('id' in item) {
          return item.id !== action.payload;
        }
        return true;
      });
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    adjustItemQuantity: (state, action: PayloadAction<{ quantity: number; id: string }>) => {
      const productId = action.payload.id;
      const indexOfProduct = state.cartProducts.findIndex((item) => item.id === productId);
      if (indexOfProduct !== -1) {
        state.cartProducts[indexOfProduct].amount = Number(action.payload.quantity);
      }
    },

    setSubtotalPrice: (state, action: PayloadAction<{ subTotal: number }>) => {
      state.subTotal = action.payload.subTotal;
    },
  },
});

export const {
  toggleCart,
  toggleWarranty,
  loadLazy,
  isHeroVisible,
  setLoader,
  setTotalSavings,
  setFinalPrice,
  setBundleUpsell,
  setProductsQuantity,
  setSavingsQuantityPrice,
  setCartProducts,
  addMainProduct,
  addItem,
  removeItem,
  setSubtotalPrice,
  setWarrantyData,
  adjustItemQuantity,
} = cartSlice.actions;

export default cartSlice.reducer;
